import { useNuxtApp } from '#app'
import { useStore } from '~/stores/main'
import type { CustomSEO } from '~/types/global'
import { oneSignalService } from '~/composables/oneSignal'

export function changeRoute(route: string, query?: object | null, params?: object | null): void {
  const app = useNuxtApp()
  const locale = app.$i18n.locale.value
  const router = useRouter()
  const routeName = `${route}___${locale}`
  const routeObject = {
    name: routeName,
    query,
    params
  }
  router.push(routeObject)
}

export function openPromotionalLink(url: string): Promise<void> {
  if (!url) return
  if (url.includes('https://')) {
    openExternalURL(url)
  } else {
    const page = url.split('?')[0].split('/')[0]
    const query = url.split('?')[1]
    const param = url.split('/')[1]

    const queryParam = new URLSearchParams(query)
    const queryObject = {}
    for (const [key, value] of queryParam.entries()) {
      queryObject[key] = value
    }

    let paramObject = {}
    switch (page) {
      case 'products-category':
        paramObject = { category: param }
        break
      case 'product-product':
        paramObject = { product: param }
        break
      case 'promotions-slug':
        paramObject = { slug: param }
        break
      default:
        paramObject = { id: parseInt(param) }
        break
    }

    changeRoute(page, queryObject, paramObject)
  }
}

export function openExternalURL(url: string): void {
  window.open(url, '_blank')
}

export function openProductPage(id?: number, slug?: string | null): Promise<void> {
  changeRoute('product-product', null, { product: slug || id })
}

export function useSeo(title?: string, description?: string, image?: string, imageAlt?: string) {
  const { $i18n } = useNuxtApp()
  const store = useStore()
  const route = useRoute()
  const locale = $i18n.locale
  const routeName = route.name
  if (routeName) {
    const findCustomSEO = store.customSEO?.find((seo: CustomSEO) => seo.url === routeName.slice(0, -5))
    if (findCustomSEO) {
      title = findCustomSEO.title
      description = findCustomSEO.description
      image = findCustomSEO.image?.url
      imageAlt = findCustomSEO.image_alt
    }
  }

  const customImage = image || `/seo/store_${locale.value}.png`
  const pageMeta = [
    { hid: 'title', name: 'title', content: title },
    { hid: 'description', name: 'description', content: description },
    { hid: 'og:site_name', property: 'og:site_name', content: 'miio Store' },
    { hid: 'og:type', property: 'og:type', content: 'website' },
    { hid: 'og:title', property: 'og:title', content: title },
    { hid: 'og:description', property: 'og:description', content: description },
    { hid: 'og:url', property: 'og:url', content: route.fullPath },
    { hid: 'og:image', property: 'og:image', content: customImage },
    { hid: 'og:image:type', property: 'og:image:type', content: 'image/png' },
    { hid: 'og:image:alt', property: 'og:image:alt', content: imageAlt || '' },
    { hid: 'og:image:width', property: 'og:image:width', content: '1200' },
    { hid: 'og:image:height', property: 'og:image:height', content: '630' },
    { hid: 'og:locale', property: 'og:locale', content: locale.value },
    { hid: 'lang', property: 'lang', content: locale.value },
    { hid: 'twitter:title', name: 'twitter:title', content: title },
    { hid: 'twitter:description', name: 'twitter:description', content: description },
    { hid: 'twitter:image', name: 'twitter:image', content: customImage },
    { hid: 'twitter:image:alt', name: 'twitter:image:alt', content: imageAlt || '' },
    { hid: 'twitter:image:type', name: 'twitter:image:type', content: 'image/png' },
    { hid: 'twitter:image:width', name: 'twitter:image:width', content: '1200' },
    { hid: 'twitter:image:height', name: 'twitter:image:height', content: '630' },
    { hid: 'twitter:site', name: 'twitter:site', content: 'miio Store' },
    { hid: 'twitter:card', name: 'twitter:card', content: 'summary_large_image' },
    { name: 'theme-color', content: '#318877' },
    { name: 'color-scheme', content: 'light' }
  ]
  return { title, pageMeta }
}

export const socials = [
  {
    name: 'facebook',
    title: 'Miio Facebook',
    url_pt: 'https://www.facebook.com/miioelectric',
    url_en: 'https://www.facebook.com/miioelectric',
    url_es: 'https://www.facebook.com/miio.espana',
    url_fr: 'https://www.facebook.com/miio.fr'
  },
  {
    name: 'linkedin',
    title: 'Miio Linkedin',
    url_pt: 'https://www.linkedin.com/company/muvext/',
    url_en: 'https://www.linkedin.com/company/muvext/',
    url_es: 'https://www.linkedin.com/company/muvext/',
    url_fr: 'https://www.linkedin.com/company/muvext/'
  },
  {
    name: 'instagram',
    title: 'Miio Instagram',
    url_pt: 'https://www.instagram.com/miioelectric/',
    url_en: 'https://www.instagram.com/miioelectric/',
    url_es: 'https://www.instagram.com/miio.espana/',
    url_fr: 'https://www.instagram.com/miio.france/'
  }
]

interface Socials {
  name: string
  title: string
  [key: string]: string
}

export function openSocials(social: Socials) {
  const app = useNuxtApp()
  const fullLocale = 'url_' + app.$i18n.locale.value
  window.open(social[fullLocale], '_blank')
}

export function generateDynamicSEO(titles: string[], descriptions: string[]) {
  const weekInMilliseconds = 7 * 24 * 60 * 60 * 1000
  const currentWeek: number = Math.floor(Date.now() / weekInMilliseconds)

  const seed: number = currentWeek

  const randomTitleIndex = seed % titles.length
  const randomDescriptionIndex = seed % descriptions.length

  const seoTitle: string = titles[randomTitleIndex]
  const seoDescription: string = descriptions[randomDescriptionIndex]

  return {
    title: seoTitle,
    description: seoDescription
  }
}

export function checkEnabledCookies(): void {
  const { cookiesEnabledIds } = useCookieControl()
  if (cookiesEnabledIds.value?.includes('ad_storage')) {
    changePixelStatus(true)
    oneSignalService.setConsentGiven(true)
  } else {
    changePixelStatus(false)
  }
  setTimeout(() => {
    trackEvent({ event: 'policy_cookies_change' })
  }, 5000)
}

function changePixelStatus(enable: boolean) {
  const nuxtApp = useNuxtApp()
  try {
    if (enable) {
      nuxtApp.$fb.enable()
    } else {
      nuxtApp.$fb.disable()
    }
  } catch (_) {}
}

export function goToPromoPage(): void {
  const store = useStore()
  if (store.promos && store.promos?.length > 0) {
    changeRoute('promotions-slug', null, { slug: store.promos[0].seo.slug })
  }
}
